import type { AddEmployeeStep } from '@/types/add-employee'
import type { BulkUploadErrorCode } from '@/types/csv'
import type { CoverageStatus } from '@/lib/utils/employee-filters'

import { rewriteHeapEvent } from './rewrite'

/**
 * Every Usage Event and the only properties it may carry. Heap's rewrite hook
 * leaves custom properties alone, so this map and ALLOWED_KEYS are the privacy
 * boundary: ids, counts and fixed categories only, never typed text.
 */
export interface UsageEventProperties {
  pageViewed: { path: string }
  rosterSearched: { employerGroupUuid: string }
  rosterFilterChanged: {
    employerGroupUuid: string
    coverage: CoverageStatus | 'all'
  }
  employeeAddStepViewed: {
    employerGroupUuid: string
    stepName: AddEmployeeStep
    stepIndex: number
  }
  employeeAddSubmitted: { employerGroupUuid: string; dependentCount: number }
  employeeAddSucceeded: { employerGroupUuid: string }
  employeeAddFailed: {
    employerGroupUuid: string
    errorCode: 'enroll_failed' | 'enroll_threw'
  }
  // The upload modal's company is optional, so the id is too.
  censusUploadOpened: { employerGroupUuid?: string }
  censusUploadSubmitted: { employerGroupUuid?: string }
  censusUploadSucceeded: { employerGroupUuid?: string }
  censusUploadFailed: {
    employerGroupUuid?: string
    errorCode: BulkUploadErrorCode
  }
}

export type AnalyticsEvent = keyof UsageEventProperties

type EventProperties = Record<string, string>

type HeapStub = unknown[][] &
  Record<string, (...args: unknown[]) => void> & {
    appid?: string
    config?: Record<string, unknown>
  }

declare global {
  interface Window {
    heap?: HeapStub
  }
}

const HEAP_CDN = 'https://cdn.heapanalytics.com/js/heap-'

const QUEUED_METHODS = [
  'addEventProperties',
  'identify',
  'resetIdentity',
  'track',
]

let pendingLoad: Promise<void> = Promise.resolve()

/**
 * Calls queue until the script arrives; the promise settles on its load or
 * error.
 */
export function loadHeap(appId: string): Promise<void> {
  if (window.heap) {
    // Heap reads the hook at event time, so a stub made elsewhere is fixed here.
    window.heap.config = { ...window.heap.config, rewrite: rewriteHeapEvent }
    return pendingLoad
  }

  const stub = Object.assign([], {
    appid: appId,
    config: { rewrite: rewriteHeapEvent },
  }) as unknown as HeapStub
  for (const method of QUEUED_METHODS) {
    stub[method] = (...args: unknown[]) => {
      stub.push([method, ...args])
    }
  }
  window.heap = stub

  const script = document.createElement('script')
  script.async = true
  script.src = `${HEAP_CDN}${appId}.js`

  pendingLoad = new Promise<void>((resolve, reject) => {
    const fail = (reason: unknown) => {
      // A dead queue would swallow every later call and block a retry.
      if (window.heap === stub) delete window.heap
      script.remove()
      reject(reason)
    }
    script.onload = () => resolve()
    script.onerror = () => fail(new Error('Heap script failed to load'))
    try {
      document.head.appendChild(script)
    } catch (error) {
      fail(error)
    }
  })
  return pendingLoad
}

function callHeap(method: string, ...args: unknown[]): void {
  try {
    window.heap?.[method]?.(...args)
  } catch {
    // Analytics must never break the page it measures.
  }
}

export function identify(userId: string): void {
  callHeap('identify', userId)
}

export function resetIdentity(): void {
  callHeap('resetIdentity')
}

export function addEventProperties(properties: EventProperties): void {
  callHeap('addEventProperties', properties)
}

// Types are erased at runtime, so only these keys per event are ever sent.
const ALLOWED_KEYS: {
  [E in AnalyticsEvent]: readonly (keyof UsageEventProperties[E])[]
} = {
  pageViewed: ['path'],
  rosterSearched: ['employerGroupUuid'],
  rosterFilterChanged: ['employerGroupUuid', 'coverage'],
  employeeAddStepViewed: ['employerGroupUuid', 'stepName', 'stepIndex'],
  employeeAddSubmitted: ['employerGroupUuid', 'dependentCount'],
  employeeAddSucceeded: ['employerGroupUuid'],
  employeeAddFailed: ['employerGroupUuid', 'errorCode'],
  censusUploadOpened: ['employerGroupUuid'],
  censusUploadSubmitted: ['employerGroupUuid'],
  censusUploadSucceeded: ['employerGroupUuid'],
  censusUploadFailed: ['employerGroupUuid', 'errorCode'],
}

// Heap takes string values; counts are converted here so callers stay typed.
function toHeapProperties(
  keys: readonly string[],
  properties: Record<string, string | number | undefined> = {},
): Record<string, string> {
  return Object.fromEntries(
    keys
      .filter(key => properties[key] !== undefined)
      .map(key => [key, String(properties[key])]),
  )
}

// Rejects keys the event does not declare, even on a variable, where
// TypeScript's own excess-property check does not run.
type Declared<E extends AnalyticsEvent, P> = P &
  Record<Exclude<keyof P, keyof UsageEventProperties[E]>, never>

export function track<
  E extends AnalyticsEvent,
  P extends UsageEventProperties[E],
>(name: E, properties: Declared<E, P>): void {
  // Erased types let an unknown name through; with no allowlist it is dropped.
  const keys = ALLOWED_KEYS[name] as readonly string[] | undefined
  if (!keys) return
  callHeap(
    'track',
    name,
    toHeapProperties(
      keys,
      properties as Record<string, string | number | undefined>,
    ),
  )
}
